// ===== Packages =====

import {
    Selection,
}                           from 'slate';

// ===== Enums =====

import {
    ZOOM_LEVEL,
}                           from '../components/Editor/elements/enums';

// ===== Interfaces =====

import {
    IReadPostChapterItem,
    IZoomLevelItem,
}                           from '../interfaces';

const parseSerializedSlateValueLength = (value?: string): number => {
    if (value == null || value === '') {
        return 0;
    }
    try {
        const parsedValue = JSON.parse(value) as unknown;
        return Array.isArray(parsedValue) ? parsedValue.length : 0;
    } catch (_error) {
        return 0;
    }
};

/**
 * Applies a node-index delta to both endpoints of a Slate selection.
 * Returns null when the shifted selection would point before the current scope.
 */
const shiftSelectionByNodeOffset = (
    selection: Selection,
    nodeOffset: number,
): NonNullable<Selection> | null => {
    if (selection == null) {
        return null;
    }

    const shiftedAnchorNodeIndex = selection.anchor.path[0] + nodeOffset;
    const shiftedFocusNodeIndex = selection.focus.path[0] + nodeOffset;

    if (
        shiftedAnchorNodeIndex < 0
        || shiftedFocusNodeIndex < 0
    ) {
        return null;
    }

    return {
        anchor: {
            path: [
                shiftedAnchorNodeIndex,
                ...selection.anchor.path.slice(1),
            ],
            offset: selection.anchor.offset,
        },
        focus: {
            path: [
                shiftedFocusNodeIndex,
                ...selection.focus.path.slice(1),
            ],
            offset: selection.focus.offset,
        },
    };
};

/**
 * Keeps selection-candidate deduping readable at the call sites.
 */
const getSelectionCandidateKey = (selection: NonNullable<Selection>): string => JSON.stringify({
    anchorPath: selection.anchor.path,
    anchorOffset: selection.anchor.offset,
    focusPath: selection.focus.path,
    focusOffset: selection.focus.offset,
});

// ** IMPORTANT **: Keep in sync with functions/src/index.ts/getProjectedSelectionOffsetForAnnotationQuote
const getProjectedSelectionOffset = ({
    chapters,
    currentZoomLevel,
    selectedPostValuePath,
    chapterId,
    sectionId,
}: {
    chapters: IReadPostChapterItem[],
    currentZoomLevel: IZoomLevelItem,
    selectedPostValuePath: number[],
    chapterId?: string,
    sectionId?: string,
}): number => {
    if (chapterId == null) {
        return 0;
    }

    const resolvedChapterIndex = chapters.findIndex((chapter) => chapter.id === chapterId);
    if (resolvedChapterIndex === -1) {
        return 0;
    }

    const viewingProjectedChapter = (
        currentZoomLevel.level === ZOOM_LEVEL.four
        && selectedPostValuePath.length === 1
    );

    let priorNodes = 0;

    if (!viewingProjectedChapter) {
        for (let i = 0; i < resolvedChapterIndex; i += 1) {
            const priorChapter = chapters[i];
            if (priorChapter.sections != null) {
                priorNodes += priorChapter.sections.reduce((sectionNodeTotal, section) => (
                    sectionNodeTotal + parseSerializedSlateValueLength(section.value)
                ), 0);
            } else {
                priorNodes += parseSerializedSlateValueLength(priorChapter.value);
            }
        }
    }

    if (sectionId != null && chapters[resolvedChapterIndex].sections != null) {
        const resolvedSectionIndex = chapters[resolvedChapterIndex].sections!.findIndex(
            (section) => section.id === sectionId,
        );
        if (resolvedSectionIndex === -1) {
            return priorNodes;
        }

        priorNodes += chapters[resolvedChapterIndex].sections!
            .slice(0, resolvedSectionIndex)
            .reduce((sectionNodeTotal, section) => (
                sectionNodeTotal + parseSerializedSlateValueLength(section.value)
            ), 0);
    }

    return priorNodes;
};

/**
 * Resolves the possible in-editor selection coordinates for an annotation.
 * Most annotations already store scope-local paths, but older chapter/section
 * annotations can still be stored in whole-post coordinates. Callers can try
 * the candidates in order and stop at the first one that exists in the current
 * rendered editor tree.
 */
export const resolvePostSelectionCandidates = (
    selection: Selection,
    chapters: IReadPostChapterItem[] | undefined,
    currentZoomLevel: IZoomLevelItem | null,
    selectedPostValuePath: number[],
    chapterId?: string,
    sectionId?: string,
) : NonNullable<Selection>[] => {
    if (selection == null) {
        return [];
    }

    const candidates: NonNullable<Selection>[] = [selection];

    if (
        !chapters
        || !currentZoomLevel
        || chapterId == null
    ) {
        return candidates;
    }

    const priorNodes = getProjectedSelectionOffset({
        chapters,
        currentZoomLevel,
        selectedPostValuePath,
        chapterId,
        sectionId,
    });

    if (currentZoomLevel.level !== ZOOM_LEVEL.five) {
        const projectedSelection = shiftSelectionByNodeOffset(selection, priorNodes);
        return projectedSelection ? [projectedSelection] : candidates;
    }

    if (selectedPostValuePath.length === 0 || priorNodes === 0) {
        return candidates;
    }

    // Older chapter/section annotations can still point at the whole-post node
    // space even though the current read-only editor only renders the scoped
    // chapter/section body. Keep the local selection first, then offer the
    // rebased legacy candidate as a compatibility fallback.
    const scopedLegacySelection = shiftSelectionByNodeOffset(selection, -priorNodes);
    if (!scopedLegacySelection) {
        return candidates;
    }

    const existingCandidateKeys = new Set(candidates.map((candidate) => (
        getSelectionCandidateKey(candidate)
    )));
    const legacyCandidateKey = getSelectionCandidateKey(scopedLegacySelection);

    if (!existingCandidateKeys.has(legacyCandidateKey)) {
        candidates.push(scopedLegacySelection);
    }

    return candidates;
};

/**
 * Preserves the historical single-selection contract for older callers.
 */
const normalizePostSelectionPath = (
    selection: Selection,
    chapters: IReadPostChapterItem[] | undefined,
    currentZoomLevel: IZoomLevelItem | null,
    selectedPostValuePath: number[],
    chapterId?: string,
    sectionId?: string,
): Selection => resolvePostSelectionCandidates(
    selection,
    chapters,
    currentZoomLevel,
    selectedPostValuePath,
    chapterId,
    sectionId,
)[0];

export default normalizePostSelectionPath;
